<template>
  <div
    class="sidebar-topic"
    :class="{ disabled: disabled }"
    ref="container"
  >
    <div class="sidebar-topic-header">
      <h3>{{ step }} - {{ title }}</h3>
    </div>

    <div class="sidebar-topic-help-text">
      <p>
        {{ description }}
      </p>
    </div>

    <div class="sidebar-topic-action-zone">
      <button
        class="call-to-action"
        :aria-label="`Select the ${title}`"
        :disabled="disabled"
        @click="openQuestions()"
      >
        Select <i class="bi bi-chevron-down" />
      </button>
      <button
        v-if="selectedTopic"
        :aria-label="`Clear the ${title}`"
        :disabled="disabled"
        @click="clearSelection()"
      >
        Clear <i class="bi bi-x-circle-fill" />
      </button>
    </div>
    <div
      v-if="disabled"
      class="sidebar-topic-disabled-message"
    >
      <i>Selecting a {{ title }} is unavailable until a Primary Question is selected.</i>
    </div>
    <question-list
      v-if="areQuestionsOpen"
      :use-sub-selection="useSubSelection"
      :selected-levels="selectedLevels"
      :selected-question="selectedTopic"
      @onSelectQuestion="selectQuestion($event)"
      @onClosePopup="closeQuestions()"
      @onListOpen="listOpened()"
    />

    <div class="sidebar-topic-selection">
      <dl v-if="selectedTopic">
        <div>
          <dt>Selected question:</dt>
          <dd>
            {{ selectedTopic.label }}
          </dd>
        </div>
        <template v-if="useSubSelection && selectedLevels">
          <div>
            <dt>Selected categories:</dt>
            <dd>
              <ul>
                <li
                  v-for="level in selectedLevels.levels"
                  :key="level.value"
                >
                  {{ level.label }}
                </li>
              </ul>
            </dd>
          </div>
        </template>
        <template v-if="selectedTopic">
          <div>
            <dt class="detail-label">
              Question group:
            </dt>
            <dd class="detail-value">
              {{ selectedTopic.group }}
            </dd>
          </div>
          <div>
            <dt class="detail-label">
              PUF variable name:
            </dt>
            <dd class="detail-value">
              [{{ selectedTopic.id }}]
            </dd>
          </div>
        </template>
      </dl>
      <p
        v-else
        class="no-selection"
      >
        No {{ title }} is selected.<br>
        <span :class="{ 'selection-required-message': selectionRequired }">
          <i>This selection is {{ selectionRequired ? "required" : "optional" }}.</i>
        </span>
      </p>
    </div>
  </div>
</template>

<script>
import QuestionList from './QuestionPopup.vue'
export default {
  name: 'SidebarTopic',
  components: { QuestionList },
  props: {
    step: {
      type: Number,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    description: {
      type: String,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    selectionRequired: {
      type: Boolean,
      default: false
    },
    selectedTopic: {
      type: Object,
      default: null
    },
    selectedLevels: {
      type: Object,
      default: null
    },
    useSubSelection: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      areQuestionsOpen: false
    }
  },
  methods: {
    selectQuestion (value) {
      this.$emit('onSelectTopic', value)
      this.closeQuestions()
    },
    openQuestions () {
      this.areQuestionsOpen = true
    },
    closeQuestions () {
      this.areQuestionsOpen = false
    },
    clearSelection () {
      this.$emit('onSelectTopic', { id: null, levels: null })
      this.closeQuestions()
    },
    listOpened () {
      const y = this.$refs.container.getBoundingClientRect().top
      const height = window.visualViewport.height

      if (y / height > 0.4) {
        window.scrollTo({
          left: 0,
          top: this.$refs.container.offsetTop,
          behavior: 'smooth'
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.sidebar-topic {
  margin: 24px 0;
  padding: 4px;
  background-color: #edf1f7;
}

.sidebar-topic:first-child {
  margin-top: 0;
}

.sidebar-topic-header {
  padding: 8px 8px;

  h3 {
    font-family: Merriweather;
    font-size: 16px;
    font-weight: bold;
    margin-top: 0;
    color: #323a45;
  }
}

.sidebar-topic-help-text {
  padding: 0px 8px;
  font-size: 14px;

  p {
    margin-bottom: 0;
  }
}

.sidebar-topic-selection {
  padding: 12px 8px;
  background-color: #fff;

  dl {
    margin-bottom: 0;
  }

  p {
    margin-bottom: 0;
  }

  .no-selection {
    font-size: 16px;
  }

  .selection-required-message {
    color: #981b1e;
  }
}

dt {
  font-family: Merriweather;
  font-weight: bold;
}

dd {
  margin-left: 1rem;
  font-size: 16px;
}

dd > ul {
  list-style-position: inside;
  padding-left: 0;
}

.detail-label,
.detail-value {
  display: inline-block;
  margin-left: 0;
  margin-bottom: 0;
}

.detail-label {
  font-size: 12px;
}

.detail-value {
  margin-left: 0.5rem;
  font-size: 14px;
}

.sidebar-topic-action-zone {
  padding: 12px 8px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.sidebar-topic-disabled-message {
  padding: 0px 8px 12px 8px;
}

.disabled {
  background-color: #f8f9fc;
  color: #737373;

  h3 {
    color: #737373;
  }
}

button {
  border: 0px;
  color: #313a45;
  background: none;
  transition: 300ms ease all;
  font-size: 1.1em;

  i {
    color: #267cb8;
    transform: scale(1.2);
    transform-origin: left;
    display: inline-block;
    transition: 300ms ease all;
  }

  &:hover {
    color: #267cb8;
    i {
      color: #205492;
      transform: scale(1.45);
    }
  }

  &:disabled {
    color: #737373;

    i {
      color: #aaa;
      transform: scale(1.2);
    }
  }

  &.call-to-action {
    padding: 4px 12px;
    color: #fff;
    background-color: #267cb8;
    transition: 300ms ease all;

    i {
      color: #fff;
      transform: scale(1.0);
      transform-origin: left;
      display: inline-block;
      transition: 300ms ease all;
    }

    &:hover {
      background-color: #205492;
      i {
        transform: scale(1.0);
      }
    }

    &:disabled {
      color: #fff;
      background-color: #aaa;

      i {
        color: #fff;
      }
    }
  }
}
</style>
